// VOY-36: Request Management - Debounce, AbortController, Queue, Retry Logic
// VOY-333: Enhanced retry strategy for ERR_CONNECTION_CLOSED errors
//
// ============================================================================
// VOY-333 ROOT CAUSE ANALYSIS & TIMEOUT ALIGNMENT
// ============================================================================
//
// Problem:
// - Hotel search requests to POST /api/hotels/search/destination fail with
//   net::ERR_CONNECTION_CLOSED in the browser console
// - First 1-2 requests fail, but retries eventually succeed
//
// Root Cause:
// - Backend hotel search takes ~7s (RateHawk API call + data enrichment)
// - Render.com proxy/load balancer may close idle connections or have
//   instability during long-running requests
// - Browser sees connection closed before response arrives
//
// Solution:
// 1. Enhanced retry strategy:
//    - Increased maxRetries from 3 to 4 for connection instability
//    - Specific detection for connection closed errors (ERR_CONNECTION_CLOSED,
//      Failed to fetch, etc.)
//    - Longer initial delay (2s) for connection errors to allow recovery
//    - Extended max delay (15s) for exponential backoff
//
// 2. User feedback during retries:
//    - onRetry callback passed through fetchWithRetry to UI components
//    - SearchResults shows "Connection issue detected. Retrying..." message
//    - Amber-colored feedback bar during retry attempts
//
// Timeout Alignment:
// - Browser default fetch timeout: ~60s
// - Render proxy timeout: ~30s
// - Backend RateHawk API + enrichment: ~7s typical
// - Retry delays: 2s (connection) or 1s (other) -> then 4s -> 8s -> 15s (max) via exponential backoff
// - Total worst-case retry time: ~29s (within proxy timeout)
//
// If proxy timeout continues to be an issue, consider:
// - Adding HOTEL_SEARCH_TIMEOUT_MS env variable for BE to abort early
// - Implementing streaming/SSE for long searches
// - Edge caching for frequent destinations
// ============================================================================

/**
 * Debounce a function call
 * @param func Function to debounce
 * @param wait Wait time in milliseconds
 * @returns Debounced function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(() => {
      func(...args);
      timeout = null;
    }, wait);
  };
}

/**
 * Request Queue Manager - Limits concurrent requests
 */
class RequestQueue {
  private queue: Array<() => Promise<any>> = [];
  private running: number = 0;
  private maxConcurrent: number;

  constructor(maxConcurrent: number = 3) {
    this.maxConcurrent = maxConcurrent;
  }

  /**
   * Add a request to the queue
   */
  async add<T>(requestFn: () => Promise<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      const execute = async () => {
        this.running++;
        try {
          const result = await requestFn();
          resolve(result);
        } catch (error) {
          reject(error);
        } finally {
          this.running--;
          this.processNext();
        }
      };

      this.queue.push(execute);
      this.processNext();
    });
  }

  private processNext() {
    if (this.running < this.maxConcurrent && this.queue.length > 0) {
      const next = this.queue.shift();
      if (next) {
        next();
      }
    }
  }

  /**
   * Get current queue size
   */
  size(): number {
    return this.queue.length;
  }

  /**
   * Get number of running requests
   */
  runningCount(): number {
    return this.running;
  }

  /**
   * Clear all pending requests
   */
  clear() {
    this.queue = [];
  }
}

// Global request queue (max 3 concurrent)
export const globalRequestQueue = new RequestQueue(3);

/**
 * Retry configuration
 */
interface RetryConfig {
  maxRetries?: number;
  initialDelay?: number;
  maxDelay?: number;
  backoffMultiplier?: number;
  shouldRetry?: (error: any) => boolean;
  /** VOY-333: Callback for retry events - useful for UI feedback */
  onRetry?: (attempt: number, maxRetries: number, error: any, isConnectionError: boolean) => void;
}

/**
 * VOY-333: Detect connection closed / network errors that warrant extended retry delays
 * These errors typically occur when:
 * - Render proxy closes connection before backend responds (~7s response time)
 * - Network instability causes premature connection termination
 * - Load balancer timeouts
 */
function isConnectionClosedError(error: any): boolean {
  if (!error) return false;
  const message = (error.message || '').toLowerCase();
  const name = (error.name || '').toLowerCase();

  return (
    message.includes('err_connection_closed') ||
    message.includes('connection closed') ||
    message.includes('network error') ||
    message.includes('failed to fetch') ||
    message.includes('load failed') ||
    message.includes('networkerror') ||
    (name === 'typeerror' && message.includes('fetch')) ||
    // Chrome-specific error messages
    message.includes('net::err_') ||
    message.includes('the network connection was lost')
  );
}

/**
 * Default retry configuration
 * VOY-333: Enhanced retry strategy for connection closed errors
 *
 * Root cause analysis:
 * - Backend hotel search takes ~7s (RateHawk API + enrichment)
 * - Render proxy has ~30s timeout, but connection instability can cause early closure
 * - First 1-2 requests often fail with ERR_CONNECTION_CLOSED, retries succeed
 *
 * Strategy:
 * - 4 retries (up from 3) to handle intermittent connection issues
 * - Longer initial delay (2s) for connection errors to allow proxy/network recovery
 * - Extended max delay (15s) to avoid overwhelming during recovery
 */
const defaultRetryConfig: RetryConfig = {
  maxRetries: 4, // VOY-333: Increased from 3 to handle connection instability
  initialDelay: 1000, // 1s for normal errors
  maxDelay: 15000, // VOY-333: Increased from 10s to 15s for connection recovery
  backoffMultiplier: 2,
  shouldRetry: (error: any) => {
    // Retry on network errors, 5xx errors, rate limits
    if (error.name === 'AbortError') return false; // Don't retry aborted requests
    if (error.statusCode && error.statusCode >= 500) return true;
    if (error.statusCode === 429) return true; // Rate limit
    // VOY-333: Enhanced detection for connection/network errors
    if (isConnectionClosedError(error)) return true;
    if (error.message?.includes('fetch') || error.message?.includes('network')) return true;
    return false;
  },
};

/**
 * Sleep helper
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Retry a function with exponential backoff
 * VOY-333: Enhanced with connection error detection and UI callback support
 * @param fn Function to retry
 * @param config Retry configuration
 * @returns Result of the function
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  config: RetryConfig = {}
): Promise<T> {
  const {
    maxRetries = defaultRetryConfig.maxRetries!,
    initialDelay = defaultRetryConfig.initialDelay!,
    maxDelay = defaultRetryConfig.maxDelay!,
    backoffMultiplier = defaultRetryConfig.backoffMultiplier!,
    shouldRetry = defaultRetryConfig.shouldRetry!,
    onRetry,
  } = config;

  let lastError: any;
  let delay = initialDelay;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Check if we should retry
      if (attempt >= maxRetries || !shouldRetry(error)) {
        throw error;
      }

      // VOY-333: Detect connection closed errors for enhanced handling
      const connectionError = isConnectionClosedError(error);

      // VOY-333: Use longer initial delay for connection errors
      // This gives proxy/network time to recover before retry
      const effectiveDelay = connectionError && attempt === 0
        ? Math.max(delay, 2000) // At least 2s for first retry on connection error
        : delay;

      // Calculate delay with exponential backoff
      const currentDelay = Math.min(effectiveDelay, maxDelay);

      // VOY-333: Log with connection error context
      console.log(
        `[RequestManager] Retry attempt ${attempt + 1}/${maxRetries} after ${currentDelay}ms` +
          (connectionError ? ' (connection closed)' : ''),
        error
      );

      // VOY-333: Notify UI about retry for user feedback
      if (onRetry) {
        onRetry(attempt + 1, maxRetries, error, connectionError);
      }

      await sleep(currentDelay);
      // Compound from effectiveDelay so backoff is 2s->4s->8s->15s (not 2s->2s->4s->8s)
      delay = effectiveDelay * backoffMultiplier;
    }
  }

  // Unreachable: loop always returns or throws; required for type narrowing
  throw lastError;
}

/**
 * AbortController manager for cancelling requests
 */
export class AbortManager {
  private controllers: Map<string, AbortController> = new Map();

  /**
   * Create a new AbortController for a key
   * Cancels any existing controller with the same key
   */
  create(key: string): AbortController {
    // Cancel existing controller if present
    this.abort(key);

    const controller = new AbortController();
    this.controllers.set(key, controller);
    return controller;
  }

  /**
   * Abort a request by key
   */
  abort(key: string): void {
    const controller = this.controllers.get(key);
    if (controller) {
      controller.abort();
      this.controllers.delete(key);
    }
  }

  /**
   * Abort all requests
   */
  abortAll(): void {
    this.controllers.forEach((controller) => controller.abort());
    this.controllers.clear();
  }

  /**
   * Get signal for a key (creates if doesn't exist)
   */
  getSignal(key: string): AbortSignal {
    let controller = this.controllers.get(key);
    if (!controller) {
      controller = this.create(key);
    }
    return controller.signal;
  }

  /**
   * Clean up a controller after request completes
   */
  cleanup(key: string): void {
    this.controllers.delete(key);
  }

  /**
   * Get number of active controllers
   */
  activeCount(): number {
    return this.controllers.size;
  }
}

// Global abort manager
export const globalAbortManager = new AbortManager();

// VOY-333: Export for use in error handling
export { isConnectionClosedError };

/**
 * Fetch with retry, queue, and abort support
 * VOY-333: Added onRetry support for UI feedback during retries
 */
export async function fetchWithRetry(
  url: string,
  options: RequestInit & { abortKey?: string; skipQueue?: boolean; onRetry?: RetryConfig['onRetry'] } = {},
  retryConfig?: RetryConfig
): Promise<Response> {
  const { abortKey, skipQueue, onRetry, ...fetchOptions } = options;

  // Get abort signal if key provided
  if (abortKey) {
    const controller = globalAbortManager.create(abortKey);
    fetchOptions.signal = controller.signal;
  }

  const fetchFn = async () => {
    try {
      const response = await fetch(url, fetchOptions);
      
      // Clean up abort controller on success
      if (abortKey) {
        globalAbortManager.cleanup(abortKey);
      }

      return response;
    } catch (error: any) {
      // Clean up abort controller on error
      if (abortKey) {
        globalAbortManager.cleanup(abortKey);
      }

      // Don't wrap AbortError - let it propagate as-is
      if (error instanceof Error && error.name === 'AbortError') {
        throw error;
      }

      // Enhance error with status code if available
      if (error.response) {
        error.statusCode = error.response.status;
      }

      throw error;
    }
  };

  // Apply retry logic with optional onRetry callback
  const mergedRetryConfig: RetryConfig = {
    ...retryConfig,
    // VOY-333: Pass onRetry callback to retry logic for UI feedback
    onRetry: onRetry || retryConfig?.onRetry,
  };
  const retryFn = () => retryWithBackoff(fetchFn, mergedRetryConfig);

  // Add to queue if not skipped
  if (skipQueue) {
    return retryFn();
  }

  return globalRequestQueue.add(retryFn);
}

